/**
 * Шапка панели ИИ по образцу ChatGPT/Claude (agroai-6al.61): слева «Беседы»,
 * по центру название беседы (клик — переименовать на месте), справа размер
 * панели, меню «⋯» и закрытие.
 */

import { useState } from 'react';
import { Maximize2, Menu, PanelRight, PanelRightOpen, X } from 'lucide-react';
import { Icon } from '../astra/ui/Icon';
import { InlineTitle, KebabMenu } from './KebabMenu';
import { defineMessages } from '../i18n/define';
import { useT } from '../i18n/hooks';

const m = defineMessages({
  sideLabel: { id: 'ai.header.size.side', defaultMessage: 'Боковая', description: 'Размер панели ИИ: узкая боковая; подпись кнопки для диктора' },
  sideHint: { id: 'ai.header.size.sideHint', defaultMessage: 'Боковая панель', description: 'Подсказка кнопки размера панели ИИ: боковая панель' },
  wideLabel: { id: 'ai.header.size.wide', defaultMessage: 'Широкая', description: 'Размер панели ИИ: широкая; подпись кнопки для диктора' },
  wideHint: { id: 'ai.header.size.wideHint', defaultMessage: 'Половина окна', description: 'Подсказка кнопки размера панели ИИ: панель занимает половину окна' },
  focusLabel: { id: 'ai.header.size.focus', defaultMessage: 'Фокус', description: 'Размер панели ИИ: режим фокуса; подпись кнопки для диктора' },
  focusHint: { id: 'ai.header.size.focusHint', defaultMessage: 'Во весь экран', description: 'Подсказка кнопки размера панели ИИ: панель на весь экран' },
  conversations: { id: 'ai.header.conversations', defaultMessage: 'Беседы', description: 'Кнопка слева в шапке панели ИИ: открыть список бесед и отчётов; текст и подпись для диктора, коротко' },
  conversationsHint: { id: 'ai.header.conversationsHint', defaultMessage: 'Беседы и отчёты', description: 'Подсказка кнопки «Беседы» в шапке панели ИИ' },
  titleLabel: { id: 'ai.header.titleLabel', defaultMessage: 'Название беседы', description: 'Подпись поля переименования беседы в шапке панели ИИ (для диктора)' },
  rename: { id: 'ai.header.rename', defaultMessage: 'Переименовать', description: 'Подсказка названия беседы и пункт меню «⋯»: переименовать беседу' },
  newChat: { id: 'ai.header.newChat', defaultMessage: 'Новая беседа', description: 'Подсказка названия в шапке, пока беседа ещё не началась' },
  titleAria: { id: 'ai.header.titleAria', defaultMessage: 'Название беседы: {title}. Переименовать', description: 'Подпись для диктора кнопки с названием беседы; {title} — название' },
  sizeAria: { id: 'ai.header.sizeAria', defaultMessage: 'Размер панели', description: 'Подпись для экранного диктора: группа кнопок размера панели ИИ' },
  actionsAria: { id: 'ai.header.actionsAria', defaultMessage: 'Действия с беседой', description: 'Подпись для экранного диктора: меню «⋯» с действиями над беседой' },
  unpin: { id: 'ai.header.unpin', defaultMessage: 'Открепить', description: 'Пункт меню беседы: убрать закрепление' },
  pin: { id: 'ai.header.pin', defaultMessage: 'Закрепить', description: 'Пункт меню беседы: закрепить беседу вверху списка' },
  deleteChat: { id: 'ai.header.deleteChat', defaultMessage: 'Удалить беседу', description: 'Пункт меню беседы: удалить беседу (опасное действие)' },
  confirmDelete: { id: 'ai.header.confirmDelete', defaultMessage: 'Подтвердить удаление', description: 'Второй шаг пункта «Удалить беседу»: подтверждение удаления' },
  closePanel: { id: 'ai.header.closePanel', defaultMessage: 'Закрыть панель', description: 'Подпись для диктора кнопки «×» в шапке панели ИИ' },
});

export type PanelSize = 'side' | 'wide' | 'focus';

const SIZES = [
  { id: 'side', label: m.sideLabel, hint: m.sideHint, icon: PanelRight },
  { id: 'wide', label: m.wideLabel, hint: m.wideHint, icon: PanelRightOpen },
  { id: 'focus', label: m.focusLabel, hint: m.focusHint, icon: Maximize2 },
] as const;

export interface PanelHeaderProps {
  title: string;
  hasChat: boolean;
  pinned: boolean;
  size: PanelSize;
  drawerOpen: boolean;
  onSize: (size: PanelSize) => void;
  onDrawer: () => void;
  onRename: (title: string) => void;
  onTogglePin: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function PanelHeader(props: PanelHeaderProps) {
  const t = useT();
  const { title, hasChat, pinned, size } = props;
  const [editing, setEditing] = useState(false);
  return (
    <header className="agent-panel__head">
      <button
        type="button"
        className={`btn btn--ghost btn--sm focus-ring agent-head__drawer${props.drawerOpen ? ' agent-head-on' : ''}`}
        onClick={props.onDrawer}
        aria-pressed={props.drawerOpen}
        aria-label={t(m.conversations)}
        title={t(m.conversationsHint)}
      >
        <Icon icon={Menu} size={16} />
        <span>{t(m.conversations)}</span>
      </button>
      <div className="agent-head__title">
        {editing && hasChat ? (
          <InlineTitle
            value={title}
            label={t(m.titleLabel)}
            onSave={(next) => {
              setEditing(false);
              props.onRename(next);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <button
            type="button"
            className="agent-head__name focus-ring"
            disabled={!hasChat}
            onClick={() => setEditing(true)}
            title={hasChat ? t(m.rename) : t(m.newChat)}
            aria-label={t(m.titleAria, { title })}
          >
            {title}
          </button>
        )}
      </div>
      <div className="agent-panel__actions">
        <div className="agent-size" role="radiogroup" aria-label={t(m.sizeAria)}>
          {SIZES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`btn btn--ghost btn--sm focus-ring${size === item.id ? ' agent-head-on' : ''}`}
              role="radio"
              aria-checked={size === item.id}
              aria-label={t(item.label)}
              title={t(item.hint)}
              onClick={() => props.onSize(item.id)}
            >
              <Icon icon={item.icon} size={16} />
            </button>
          ))}
        </div>
        <KebabMenu
          label={t(m.actionsAria)}
          disabled={!hasChat}
          items={[
            { id: 'pin', label: pinned ? t(m.unpin) : t(m.pin), onSelect: props.onTogglePin },
            { id: 'rename', label: t(m.rename), onSelect: () => setEditing(true) },
            { id: 'delete', label: t(m.deleteChat), confirm: t(m.confirmDelete), danger: true, onSelect: props.onDelete },
          ]}
        />
        <button type="button" className="btn btn--ghost btn--sm focus-ring" onClick={props.onClose} aria-label={t(m.closePanel)}>
          <Icon icon={X} size={16} />
        </button>
      </div>
    </header>
  );
}
